
import { AppApprovalBar } from "../components/app-approval-bar.js";
import { AppButton } from "../components/app-button.js";
import { AppChatView } from "../components/app-chat-view.js";
import { AppPromptInput } from "../components/app-prompt-input.js";
import { AppQuizBar } from "../components/app-quiz-bar.js";
import { AppDialog } from "../components/app-dialog.js";
import { IconPanelRight } from "../components/icons/panel-right.js";
import { AppSidebar, AppSidebarToggle, type SidebarProject, type SidebarThread } from "../components/app-sidebar.js";
import { AppSidebarProvider } from "../components/app-sidebar-primitives.js";
import { AppSettingsDialog } from "../components/app-settings-dialog.js";
import { type ChatTurn } from "../shared/timeline.js";
import { type ModelOption } from "../shared/model-option.js";
import { mountThreadsPage } from "../components/threads-page-controller.js";
import { threadView } from "../stores/thread-view.js";
import { sidePanel } from "../stores/side-panel.js";

interface ThreadPageData {
  username: string;
  fullName: string;
  threadId: string;
  currentTitle: string;
  selectedModel: string;
  models: ModelOption[];
  selectedModelLabel: string;
  selectedModelReasoningEffort: string;
  running: boolean;
  projects: SidebarProject[];
  threads: SidebarThread[];
  turns: ChatTurn[];
}

declare function loadThreadPage(threadId: string): ThreadPageData;

const styles = css`
  :host {
    display: block;
    height: 100%;
    min-width: 0;
    width: 100%;
  }
  .page { display: flex; height: 100%; width: 100%; }
  nav { height: 100%; }
  main { align-items: stretch; display: flex; flex: 1; flex-direction: column; height: 100%; min-width: 0; overflow: hidden; position: relative; }
  header { align-items: center; border-bottom: 1px solid var(--border); box-sizing: border-box; display: flex; height: 48px; padding: 8px; width: 100%; }
  header app-sidebar-toggle { display: none; }
  .toolbar-spacer { flex: 1; }
  .panel-button { color: var(--muted-foreground); }
  .panel-button[hidden] { display: none; }
  .thread-menu-button { margin-left: 4px; }
  .content { flex: 1; min-height: 0; width: 100%; }
  .composer-layer { align-items: center; bottom: 0; box-sizing: border-box; display: flex; flex-direction: column; left: 50%; padding: 0 0 12px; position: absolute; transform: translateX(-50%); width: min(870px, calc(100% - 32px)); z-index: 10; }
  .composer-layer app-prompt-input, .composer-layer app-approval-bar, .composer-layer app-quiz-bar { width: min(870px, 100%); }
  app-side-panel:not([open]) { display: none; }
  app-chat-view { height: 100%; }
  .error { color: var(--destructive); font-size: 13px; margin: 0 auto 6px; max-width: 860px; }
  .error:empty { display: none; }
  @media (max-width: 767px) {
    header app-sidebar-toggle { display: inline-flex; }
    header { justify-content: space-between; }
    .panel-button { display: none; }
    .composer-layer { padding-bottom: 8px; width: calc(100% - 24px); }
  }
  @media (min-width: 768px) {
    .thread-menu-button { display: none; }
  }
`;

export class ThreadsPageView extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof ThreadsPageView)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["threadid","data-thread-id"];

  #threadId = "";
  #threadIdSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #textStart8;
  #textEnd8;
  #textLast8;
  #textStart9;
  #textEnd9;
  #textLast9;
  #textStart10;
  #textEnd10;
  #textLast10;
  #textStart11;
  #textEnd11;
  #textLast11;
  #textStart12;
  #textEnd12;
  #textLast12;
  #textStart13;
  #textEnd13;
  #textLast13;
  #textStart14;
  #textEnd14;
  #textLast14;
  #textStart15;
  #textEnd15;
  #textLast15;
  #attr0;
  #attr1;
  #attr2;
  #attr3;
  #attr4;
  #attr5;
  #attr6;
  #attr7;
  #attr8;
  #attr9;
  #attr10;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #attrLast5;
  #attrLast6;
  #attrLast7;
  #attrLast8;
  #attrLast9;
  #attrLast10;
  #attrLast11;
  #attrLast12;
  #attrLast13;
  #attrLast14;
  #attrLast15;
  #attrLast16;
  #attrLast17;
  #attrLast18;
  #attrLast19;
  #attrLast20;
  #attrLast21;
  #attrLast22;
  #attrLast23;
  #attrLast24;
  #attrLast25;
  #attrLast26;
  #attrLast27;
  #attrLast28;
  #attrLast29;
  #serverPayload;
  #storeCleanups = [];
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get threadId() {
    return this.#threadId;
  }

  set threadId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#threadId, next)) return;
    this.#threadId = next;
    this.#threadIdSet = true;
    this.#updateBindings('threadId');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-thread-id')) this.threadId = this.dataset.threadId;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.threadId = props.threadId;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#threadIdSet) this.threadId = this.dataset.threadId;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    this.#connectStores();
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => mountThreadsPage(this))();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#disconnectStores();
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'threadid':
      case 'data-thread-id':
        this.threadId = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const data = this.#server("data");
    const turns = threadView.active ? threadView.turns : data.turns;
    const running = threadView.active ? threadView.running : data.running;
    const placeholder = threadView.active ? threadView.placeholder : ((data.threadId === "") ? "Ask S.T.R.I.D.E. anything" : "Message S.T.R.I.D.E.");
    const selectedModel = threadView.active ? threadView.selectedModel : data.selectedModel;
    const models = threadView.active ? threadView.models : data.models;
    const selectedModelLabel = threadView.active ? threadView.selectedModelLabel : data.selectedModelLabel;
    const selectedModelReasoningEffort = threadView.active ? threadView.selectedModelReasoningEffort : data.selectedModelReasoningEffort;
    return `<style>${this.#escText(styles)}</style><app-sidebar-provider><!--argon-text:0-->${""}<!--/argon-text:0--><div class="page"><nav><app-sidebar data-projects="${this.#attrEsc(data.projects)}" data-threads="${this.#attrEsc(data.threads)}" data-username="${this.#attrEsc(data.username)}" data-full-name="${this.#attrEsc(data.fullName)}" data-argon-bind="0"><!--argon-text:1-->${""}<!--/argon-text:1--></app-sidebar></nav><main><header><app-sidebar-toggle><!--argon-text:2-->${""}<!--/argon-text:2--></app-sidebar-toggle><span class="toolbar-spacer"></span><app-button variant="ghost" size="icon-sm" class="panel-button" title="Open side panel" aria-label="Open side panel" data-action="side-panel-open"><!--argon-text:3-->${""}<!--/argon-text:3--><icon-panel-right><!--argon-text:4-->${""}<!--/argon-text:4--></icon-panel-right></app-button><app-button variant="ghost" size="icon-sm" class="thread-menu-button" title="Thread actions" aria-label="Thread actions" data-action="thread-menu" hidden><!--argon-text:5-->${""}⋯<!--/argon-text:5--></app-button><span data-current-title hidden><!--argon-text:6-->${this.#escText(data.currentTitle)}<!--/argon-text:6--></span></header><section class="content"><app-chat-view data-turns="${this.#attrEsc(turns)}" data-argon-bind="1"><!--argon-text:7-->${""}<!--/argon-text:7--></app-chat-view></section><div class="composer-layer" data-composer-layer><div class="error" data-error><!--argon-text:8-->${this.#escText(threadView.error)}<!--/argon-text:8--></div><app-prompt-input data-prompt${((threadView.approvalMessage !== "") || (threadView.quizQuestion !== "")) ? ' hidden' : ''} data-running="${this.#attrEsc(running)}" data-placeholder="${this.#attrEsc(placeholder)}" data-models="${this.#attrEsc(models)}" data-selected-model="${this.#attrEsc(selectedModel)}" data-selected-model-label="${this.#attrEsc(selectedModelLabel)}" data-selected-model-reasoning-effort="${this.#attrEsc(selectedModelReasoningEffort)}" data-attachments="${this.#attrEsc(threadView.attachments)}" data-argon-bind="2"><!--argon-text:9-->${""}<!--/argon-text:9--></app-prompt-input><app-approval-bar data-approval${(threadView.approvalMessage === "") ? ' hidden' : ''} data-message="${this.#attrEsc(threadView.approvalMessage)}" data-argon-bind="3"><!--argon-text:10-->${""}<!--/argon-text:10--></app-approval-bar><app-quiz-bar data-quiz${(threadView.quizQuestion === "") ? ' hidden' : ''} data-question="${this.#attrEsc(threadView.quizQuestion)}" data-options="${this.#attrEsc(threadView.quizOptions)}" data-disabled="${this.#attrEsc(threadView.quizSubmitting)}" data-argon-bind="4"><!--argon-text:11-->${""}<!--/argon-text:11--></app-quiz-bar></div></main><app-side-panel${sidePanel.open ? ' open' : ''} tabs="[{&quot;value&quot;:&quot;files&quot;,&quot;label&quot;:&quot;Files&quot;},{&quot;value&quot;:&quot;subagents&quot;,&quot;label&quot;:&quot;Subagents&quot;}]" data-active-tab="${this.#escAttr(sidePanel.tab)}" data-side-panel data-argon-bind="5"><app-button slot="header-action" variant="ghost" size="icon-sm" title="Close side panel" aria-label="Close side panel" data-action="side-panel-close"><!--argon-text:12-->${""}<!--/argon-text:12--><icon-panel-right><!--argon-text:13-->${""}<!--/argon-text:13--></icon-panel-right></app-button><app-file-explorer slot="files" data-thread-id="${this.#escAttr(data.threadId)}" data-pane-active="${sidePanel.open && (sidePanel.tab === "files")}" data-argon-bind="6"></app-file-explorer><app-subagent-view slot="subagents" data-thread-id="${this.#escAttr(data.threadId)}" data-active="${sidePanel.open && (sidePanel.tab === "subagents")}" data-argon-bind="7"></app-subagent-view></app-side-panel><app-dialog data-open="${this.#attrEsc(false)}" data-size="fullscreen" data-title="Files" data-mobile-panel data-argon-bind="8"><!--argon-text:14-->${""}<!--/argon-text:14--><app-file-explorer data-thread-id="${this.#escAttr(data.threadId)}" data-pane-active="${false}" data-mobile-files data-argon-bind="9"></app-file-explorer><app-subagent-view data-thread-id="${this.#escAttr(data.threadId)}" data-active="${false}" data-mobile-subagents data-argon-bind="10"></app-subagent-view></app-dialog><app-settings-dialog><!--argon-text:15-->${""}<!--/argon-text:15--></app-settings-dialog></div></app-sidebar-provider>`;
  }


  #server(name) {
    if (!this.#serverPayload) {
      const encoded = this.getAttribute('data-argon-server');
      if (!encoded) throw new Error('Argon server binding "' + name + '" needs SSR hydration data.');
      this.#serverPayload = JSON.parse(encoded);
    }
    if (!(name in this.#serverPayload)) throw new Error('Argon server binding "' + name + '" is missing from hydration data.');
    const value = this.#serverPayload[name];
    if (value && typeof value === 'object' && !Object.prototype.hasOwnProperty.call(value, 'refresh')) {
      Object.defineProperty(value, 'refresh', { enumerable: false, value: () => this.#refreshServer(name) });
    }
    return value;
  }

  async #refreshServer(name) {
    const response = await fetch('/__argon/threads-page-view/' + encodeURIComponent(name), { method: 'POST' });
    if (!response.ok) throw new Error('Argon server refresh failed: ' + response.status);
    this.#serverPayload[name] = await response.json();
    this.#updateBindings();
    return this.#server(name);
  }


  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
        case 'argon-text:8':
          this.#textStart8 = marker;
          break;
        case '/argon-text:8':
          this.#textEnd8 = marker;
          break;
        case 'argon-text:9':
          this.#textStart9 = marker;
          break;
        case '/argon-text:9':
          this.#textEnd9 = marker;
          break;
        case 'argon-text:10':
          this.#textStart10 = marker;
          break;
        case '/argon-text:10':
          this.#textEnd10 = marker;
          break;
        case 'argon-text:11':
          this.#textStart11 = marker;
          break;
        case '/argon-text:11':
          this.#textEnd11 = marker;
          break;
        case 'argon-text:12':
          this.#textStart12 = marker;
          break;
        case '/argon-text:12':
          this.#textEnd12 = marker;
          break;
        case 'argon-text:13':
          this.#textStart13 = marker;
          break;
        case '/argon-text:13':
          this.#textEnd13 = marker;
          break;
        case 'argon-text:14':
          this.#textStart14 = marker;
          break;
        case '/argon-text:14':
          this.#textEnd14 = marker;
          break;
        case 'argon-text:15':
          this.#textStart15 = marker;
          break;
        case '/argon-text:15':
          this.#textEnd15 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#attr3 = root.querySelector('[data-argon-bind="3"]');
    this.#attr4 = root.querySelector('[data-argon-bind="4"]');
    this.#attr5 = root.querySelector('[data-argon-bind="5"]');
    this.#attr6 = root.querySelector('[data-argon-bind="6"]');
    this.#attr7 = root.querySelector('[data-argon-bind="7"]');
    this.#attr8 = root.querySelector('[data-argon-bind="8"]');
    this.#attr9 = root.querySelector('[data-argon-bind="9"]');
    this.#attr10 = root.querySelector('[data-argon-bind="10"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #connectStores() {
    if (this.#storeCleanups.length > 0) return;
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.active", () => this.#updateBindings("store:../stores/thread-view.js:threadView.active")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.turns", () => this.#updateBindings("store:../stores/thread-view.js:threadView.turns")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.running", () => this.#updateBindings("store:../stores/thread-view.js:threadView.running")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.placeholder", () => this.#updateBindings("store:../stores/thread-view.js:threadView.placeholder")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.selectedModel", () => this.#updateBindings("store:../stores/thread-view.js:threadView.selectedModel")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.models", () => this.#updateBindings("store:../stores/thread-view.js:threadView.models")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.selectedModelLabel", () => this.#updateBindings("store:../stores/thread-view.js:threadView.selectedModelLabel")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.selectedModelReasoningEffort", () => this.#updateBindings("store:../stores/thread-view.js:threadView.selectedModelReasoningEffort")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.error", () => this.#updateBindings("store:../stores/thread-view.js:threadView.error")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.approvalMessage", () => this.#updateBindings("store:../stores/thread-view.js:threadView.approvalMessage")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.quizQuestion", () => this.#updateBindings("store:../stores/thread-view.js:threadView.quizQuestion")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.attachments", () => this.#updateBindings("store:../stores/thread-view.js:threadView.attachments")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.quizOptions", () => this.#updateBindings("store:../stores/thread-view.js:threadView.quizOptions")));
    this.#storeCleanups.push(__argonStoreSubscribe0("threadView.quizSubmitting", () => this.#updateBindings("store:../stores/thread-view.js:threadView.quizSubmitting")));
    this.#storeCleanups.push(__argonStoreSubscribe1("sidePanel.open", () => this.#updateBindings("store:../stores/side-panel.js:sidePanel.open")));
    this.#storeCleanups.push(__argonStoreSubscribe1("sidePanel.tab", () => this.#updateBindings("store:../stores/side-panel.js:sidePanel.tab")));
  }

  #disconnectStores() {
    for (const cleanup of this.#storeCleanups.splice(0)) cleanup();
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const data = this.#server("data");
    const turns = (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.turns') ? (threadView.active ? threadView.turns : data.turns) : undefined;
    const running = (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running' || prop === 'store:../stores/thread-view.js:threadView.placeholder' || prop === 'store:../stores/thread-view.js:threadView.models' || prop === 'store:../stores/thread-view.js:threadView.selectedModel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort' || prop === 'store:../stores/thread-view.js:threadView.attachments') ? (threadView.active ? threadView.running : data.running) : undefined;
    const placeholder = (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running' || prop === 'store:../stores/thread-view.js:threadView.placeholder' || prop === 'store:../stores/thread-view.js:threadView.models' || prop === 'store:../stores/thread-view.js:threadView.selectedModel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort' || prop === 'store:../stores/thread-view.js:threadView.attachments') ? (threadView.active ? threadView.placeholder : ((data.threadId === "") ? "Ask S.T.R.I.D.E. anything" : "Message S.T.R.I.D.E.")) : undefined;
    const selectedModel = (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running' || prop === 'store:../stores/thread-view.js:threadView.placeholder' || prop === 'store:../stores/thread-view.js:threadView.models' || prop === 'store:../stores/thread-view.js:threadView.selectedModel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort' || prop === 'store:../stores/thread-view.js:threadView.attachments') ? (threadView.active ? threadView.selectedModel : data.selectedModel) : undefined;
    const models = (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running' || prop === 'store:../stores/thread-view.js:threadView.placeholder' || prop === 'store:../stores/thread-view.js:threadView.models' || prop === 'store:../stores/thread-view.js:threadView.selectedModel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort' || prop === 'store:../stores/thread-view.js:threadView.attachments') ? (threadView.active ? threadView.models : data.models) : undefined;
    const selectedModelLabel = (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running' || prop === 'store:../stores/thread-view.js:threadView.placeholder' || prop === 'store:../stores/thread-view.js:threadView.models' || prop === 'store:../stores/thread-view.js:threadView.selectedModel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort' || prop === 'store:../stores/thread-view.js:threadView.attachments') ? (threadView.active ? threadView.selectedModelLabel : data.selectedModelLabel) : undefined;
    const selectedModelReasoningEffort = (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running' || prop === 'store:../stores/thread-view.js:threadView.placeholder' || prop === 'store:../stores/thread-view.js:threadView.models' || prop === 'store:../stores/thread-view.js:threadView.selectedModel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort' || prop === 'store:../stores/thread-view.js:threadView.attachments') ? (threadView.active ? threadView.selectedModelReasoningEffort : data.selectedModelReasoningEffort) : undefined;
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}⋯`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${this.#escText(data.currentTitle)}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.turns') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.error') {
      const __argonNext = `${this.#escText(threadView.error)}`;
      if (__argonNext !== this.#textLast8) {
        this.#textLast8 = __argonNext;
        this.#replaceRange(this.#textStart8, this.#textEnd8, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running' || prop === 'store:../stores/thread-view.js:threadView.placeholder' || prop === 'store:../stores/thread-view.js:threadView.models' || prop === 'store:../stores/thread-view.js:threadView.selectedModel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort' || prop === 'store:../stores/thread-view.js:threadView.attachments') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast9) {
        this.#textLast9 = __argonNext;
        this.#replaceRange(this.#textStart9, this.#textEnd9, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.approvalMessage') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast10) {
        this.#textLast10 = __argonNext;
        this.#replaceRange(this.#textStart10, this.#textEnd10, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/thread-view.js:threadView.quizQuestion' || prop === 'store:../stores/thread-view.js:threadView.quizOptions' || prop === 'store:../stores/thread-view.js:threadView.quizSubmitting') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast11) {
        this.#textLast11 = __argonNext;
        this.#replaceRange(this.#textStart11, this.#textEnd11, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast12) {
        this.#textLast12 = __argonNext;
        this.#replaceRange(this.#textStart12, this.#textEnd12, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast13) {
        this.#textLast13 = __argonNext;
        this.#replaceRange(this.#textStart13, this.#textEnd13, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast14) {
        this.#textLast14 = __argonNext;
        this.#replaceRange(this.#textStart14, this.#textEnd14, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast15) {
        this.#textLast15 = __argonNext;
        this.#replaceRange(this.#textStart15, this.#textEnd15, __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.projects)}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('data-projects', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.threads)}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('data-threads', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.username)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('data-username', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.fullName)}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr0.setAttribute('data-full-name', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.turns') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(turns)}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr1.setAttribute('data-turns', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.approvalMessage' || prop === 'store:../stores/thread-view.js:threadView.quizQuestion') && this.#attr2) {
      this.#attr2.toggleAttribute('hidden', !!((threadView.approvalMessage !== "") || (threadView.quizQuestion !== "")));
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.running') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(running)}`;
      if (__argonNext !== this.#attrLast6) {
        this.#attrLast6 = __argonNext;
        this.#attr2.setAttribute('data-running', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.placeholder') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(placeholder)}`;
      if (__argonNext !== this.#attrLast7) {
        this.#attrLast7 = __argonNext;
        this.#attr2.setAttribute('data-placeholder', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.models') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(models)}`;
      if (__argonNext !== this.#attrLast8) {
        this.#attrLast8 = __argonNext;
        this.#attr2.setAttribute('data-models', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.selectedModel') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(selectedModel)}`;
      if (__argonNext !== this.#attrLast9) {
        this.#attrLast9 = __argonNext;
        this.#attr2.setAttribute('data-selected-model', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.selectedModelLabel') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(selectedModelLabel)}`;
      if (__argonNext !== this.#attrLast10) {
        this.#attrLast10 = __argonNext;
        this.#attr2.setAttribute('data-selected-model-label', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.active' || prop === 'store:../stores/thread-view.js:threadView.selectedModelReasoningEffort') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(selectedModelReasoningEffort)}`;
      if (__argonNext !== this.#attrLast11) {
        this.#attrLast11 = __argonNext;
        this.#attr2.setAttribute('data-selected-model-reasoning-effort', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.attachments') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(threadView.attachments)}`;
      if (__argonNext !== this.#attrLast12) {
        this.#attrLast12 = __argonNext;
        this.#attr2.setAttribute('data-attachments', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.approvalMessage') && this.#attr3) {
      this.#attr3.toggleAttribute('hidden', !!(threadView.approvalMessage === ""));
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.approvalMessage') && this.#attr3) {
      const __argonNext = `${this.#attrRaw(threadView.approvalMessage)}`;
      if (__argonNext !== this.#attrLast14) {
        this.#attrLast14 = __argonNext;
        this.#attr3.setAttribute('data-message', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.quizQuestion') && this.#attr4) {
      this.#attr4.toggleAttribute('hidden', !!(threadView.quizQuestion === ""));
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.quizQuestion') && this.#attr4) {
      const __argonNext = `${this.#attrRaw(threadView.quizQuestion)}`;
      if (__argonNext !== this.#attrLast16) {
        this.#attrLast16 = __argonNext;
        this.#attr4.setAttribute('data-question', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.quizOptions') && this.#attr4) {
      const __argonNext = `${this.#attrRaw(threadView.quizOptions)}`;
      if (__argonNext !== this.#attrLast17) {
        this.#attrLast17 = __argonNext;
        this.#attr4.setAttribute('data-options', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/thread-view.js:threadView.quizSubmitting') && this.#attr4) {
      const __argonNext = `${this.#attrRaw(threadView.quizSubmitting)}`;
      if (__argonNext !== this.#attrLast18) {
        this.#attrLast18 = __argonNext;
        this.#attr4.setAttribute('data-disabled', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/side-panel.js:sidePanel.open') && this.#attr5) {
      this.#attr5.toggleAttribute('open', !!(sidePanel.open));
    }
    if ((prop === undefined || prop === 'store:../stores/side-panel.js:sidePanel.tab') && this.#attr5) {
      const __argonNext = `${sidePanel.tab}`;
      if (__argonNext !== this.#attrLast20) {
        this.#attrLast20 = __argonNext;
        this.#attr5.setAttribute('data-active-tab', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr6) {
      const __argonNext = `${data.threadId}`;
      if (__argonNext !== this.#attrLast21) {
        this.#attrLast21 = __argonNext;
        this.#attr6.setAttribute('data-thread-id', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/side-panel.js:sidePanel.open' || prop === 'store:../stores/side-panel.js:sidePanel.tab') && this.#attr6) {
      const __argonNext = `${sidePanel.open && (sidePanel.tab === "files")}`;
      if (__argonNext !== this.#attrLast22) {
        this.#attrLast22 = __argonNext;
        this.#attr6.setAttribute('data-pane-active', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr7) {
      const __argonNext = `${data.threadId}`;
      if (__argonNext !== this.#attrLast23) {
        this.#attrLast23 = __argonNext;
        this.#attr7.setAttribute('data-thread-id', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/side-panel.js:sidePanel.open' || prop === 'store:../stores/side-panel.js:sidePanel.tab') && this.#attr7) {
      const __argonNext = `${sidePanel.open && (sidePanel.tab === "subagents")}`;
      if (__argonNext !== this.#attrLast24) {
        this.#attrLast24 = __argonNext;
        this.#attr7.setAttribute('data-active', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr8) {
      const __argonNext = `${this.#attrRaw(false)}`;
      if (__argonNext !== this.#attrLast25) {
        this.#attrLast25 = __argonNext;
        this.#attr8.setAttribute('data-open', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr9) {
      const __argonNext = `${data.threadId}`;
      if (__argonNext !== this.#attrLast26) {
        this.#attrLast26 = __argonNext;
        this.#attr9.setAttribute('data-thread-id', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr9) {
      const __argonNext = `${false}`;
      if (__argonNext !== this.#attrLast27) {
        this.#attrLast27 = __argonNext;
        this.#attr9.setAttribute('data-pane-active', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr10) {
      const __argonNext = `${data.threadId}`;
      if (__argonNext !== this.#attrLast28) {
        this.#attrLast28 = __argonNext;
        this.#attr10.setAttribute('data-thread-id', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr10) {
      const __argonNext = `${false}`;
      if (__argonNext !== this.#attrLast29) {
        this.#attrLast29 = __argonNext;
        this.#attr10.setAttribute('data-active', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;

    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
